/**
 * API リクエスト/レスポンススキーマ
 *
 * REST API と Durable Object のリクエスト/レスポンス
 */

import { z } from "zod";
import { NicknameSchema, RoomCodeSchema } from "./base";
import { BingoCardSchema, PartialRoomSettingsSchema } from "./room";

// ============================================
// API リクエストスキーマ
// ============================================

/** ルーム作成リクエストスキーマ */
export const CreateRoomRequestSchema = z.object({
  hostName: NicknameSchema,
  settings: PartialRoomSettingsSchema.optional(),
  /** 主催者もプレイヤーとして参加するか */
  hostJoinsAsPlayer: z.boolean().optional(),
});

/** ルーム参加リクエストスキーマ */
export const JoinRoomRequestSchema = z.object({
  nickname: NicknameSchema,
});

/** ルームコード検索スキーマ */
export const RoomCodeSearchSchema = z.object({
  code: RoomCodeSchema,
});

// ============================================
// Durable Object リクエスト/レスポンススキーマ
// ============================================

/** ルーム作成 DO リクエストスキーマ */
export const CreateRoomDORequestSchema = z.object({
  hostName: NicknameSchema,
  settings: PartialRoomSettingsSchema.optional(),
  hostJoinsAsPlayer: z.boolean().optional(),
});

/** ルーム参加 DO リクエストスキーマ */
export const JoinRoomDORequestSchema = z.object({
  nickname: NicknameSchema,
});

/** ルーム作成 DO レスポンススキーマ */
export const CreateRoomDOResponseSchema = z.object({
  roomId: z.string(),
  roomCode: z.string(),
  hostId: z.string(),
});

/** ルーム参加 DO レスポンススキーマ */
export const JoinRoomDOResponseSchema = z.object({
  participantId: z.string(),
  card: BingoCardSchema,
});

// ============================================
// API レスポンススキーマ
// ============================================

/** ルーム作成 API レスポンススキーマ */
export const CreateRoomResponseSchema = z.object({
  roomId: z.string(),
  roomCode: z.string(),
  hostId: z.string(),
  hostCard: BingoCardSchema.optional(),
});

/** ルーム参加 API レスポンススキーマ */
export const JoinRoomResponseSchema = z.object({
  participantId: z.string(),
  card: BingoCardSchema,
});

/** ルーム情報 API レスポンススキーマ */
export const RoomInfoResponseSchema = z.object({
  roomId: z.string(),
  code: z.string(),
  status: z.enum(["created", "waiting", "playing", "paused", "finished", "closed"]),
  hostNickname: z.string(),
  participantCount: z.number(),
  maxParticipants: z.number(),
  settings: z.object({
    theme: z.string(),
    allowLateJoin: z.boolean(),
  }),
});

/** ルームコード検索 API レスポンススキーマ */
export const RoomCodeLookupResponseSchema = z.object({
  roomId: z.string(),
});

/** API エラーレスポンススキーマ */
export const ApiErrorResponseSchema = z.object({
  error: z.string(),
  code: z.string().optional(),
  details: z.unknown().optional(),
  timestamp: z.string(),
});

/** API エラーレスポンス型 */
export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;

// ============================================
// 型推論
// ============================================

/** ルーム作成リクエスト型 */
export type CreateRoomRequest = z.infer<typeof CreateRoomRequestSchema>;

/** ルーム参加リクエスト型 */
export type JoinRoomRequest = z.infer<typeof JoinRoomRequestSchema>;

/** ルーム作成 DO レスポンス型 */
export type CreateRoomDOResponse = z.infer<typeof CreateRoomDOResponseSchema>;

/** ルーム参加 DO レスポンス型 */
export type JoinRoomDOResponse = z.infer<typeof JoinRoomDOResponseSchema>;
